import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Turnstile } from "./Turnstile";
import { formMessage, submitButtonLabel } from "./turnstile-ui";
import { useFormTurnstile, useLeadSubmit } from "./useLeadSubmit";

function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] };
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

// Careers applications are contact submissions (Salesforce Web-to-Lead, plus
// the HR email for page_path /careers). Also the Turnstile action.
const FORM_KEY = "contact" as const;

export function CareersForm() {
  const { send, status, error } = useLeadSubmit();
  const { token, setToken, onToken, onError, verifyFailed, resetRef, formRef, nextToken } =
    useFormTurnstile(FORM_KEY);
  const [fileError, setFileError] = useState<string | null>(null);

  async function readResume(file: File) {
    const buf = new Uint8Array(await file.arrayBuffer());
    let binary = "";
    for (const byte of buf) binary += String.fromCharCode(byte);
    return {
      filename: file.name.slice(0, 200),
      mime: file.type || "application/octet-stream",
      data: btoa(binary),
    };
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    setFileError(null);
    const fd = new FormData(e.currentTarget);
    const fullName = String(fd.get("full_name") || "").trim();
    const { first_name, last_name } = splitName(fullName);
    const jobTitle = String(fd.get("job_title") || "").trim();
    const comments = String(fd.get("comments") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim();

    const file = fd.get("resume");
    let resume: { filename: string; mime: string; data: string } | undefined;
    if (file instanceof File && file.size > 0) {
      if (file.size > 5 * 1024 * 1024) {
        setFileError("Please upload a resume smaller than 5 MB.");
        return;
      }
      resume = await readResume(file);
    }

    const sent = await send(
      {
        form_key: FORM_KEY,
        turnstile_token: token,
        first_name,
        last_name,
        email,
        phone,
        job_title: jobTitle,
        ...(resume ? { resume } : {}),
        message: `Careers inquiry\nDesired Job Title: ${jobTitle || "N/A"}\n\n${comments || "(no comments)"}`.slice(0, 2000),
      },
      { nextToken, form: formRef.current },
    );
    // A failed send spent the token: get a new one for the next tap.
    if (!sent) {
      setToken(null);
      resetRef.current?.();
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h3 className="text-xl font-semibold">Thanks for reaching out.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Our team has received your message and will follow up with you soon.
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="full_name">Your Name *</Label>
          <Input id="full_name" name="full_name" autoComplete="name" required maxLength={160} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="phone">Your Phone *</Label>
          <Input id="phone" name="phone" autoComplete="tel" type="tel" required maxLength={32} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="email">Your Email *</Label>
          <Input id="email" name="email" autoComplete="email" type="email" required maxLength={255} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="job_title">Desired Job Title</Label>
          <Input id="job_title" name="job_title" maxLength={120} />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="comments">Comments</Label>
        <Textarea id="comments" name="comments" rows={5} maxLength={1500} />
      </div>

      <div className="space-y-1">
        <Label htmlFor="resume">Upload Your Resume (PDF or Word, max 5 MB)</Label>
        <Input
          id="resume"
          name="resume"
          type="file"
          accept=".pdf,.doc,.docx,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm"
        />
        {fileError && <p className="text-sm text-destructive">{fileError}</p>}
      </div>

      {/* The action must equal form_key: submitLead checks the token's action
          against it. Careers posts as "contact" (the HR email is chosen by
          page_path /careers), so "careers" here was rejected as
          action_mismatch:careers from 2 Aug to 14 Oct 2026. */}
      <Turnstile
        appearance="interaction-only"
        onToken={onToken}
        onExpired={() => setToken(null)}
        onError={onError}
        action={FORM_KEY}
        resetRef={resetRef}
      />

      {formMessage(error, verifyFailed, token) && (
        <p className="text-sm text-destructive">{formMessage(error, verifyFailed, token)}</p>
      )}

      <Button type="submit" disabled={!token || status === "submitting"} size="lg">
        {submitButtonLabel({
          token,
          submitting: status === "submitting",
          label: "Submit",
          submittingLabel: "Submitting…",
        })}
      </Button>
    </form>
  );
}